import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { usePricing } from '../hooks/usePricing';
import { toast } from 'sonner';
import { 
  Film, 
  ArrowLeft,
  ArrowRight,
  Crown, 
  CheckCircle, 
  Zap,
  Loader2,
  X,
  FileText,
  Users,
  Download,
  Sparkles,
  ShoppingBag,
  Gift,
  Tag,
  Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Subscription = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('onetime'); // Default to one-time purchases
  const { getPrice, discountPct } = usePricing();

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Perfect for exploring your story idea',
      features: [
        { text: '2 scene generations per day', included: true },
        { text: 'First 2 beats only (Act 1 preview)', included: true },
        { text: 'Basic character tools', included: true },
        { text: 'Full 3-Act structure', included: false },
        { text: 'Script export', included: false },
        { text: 'Collaboration', included: false },
        { text: 'Pro Scene Studio', included: false },
        { text: 'Script Coverage Report', included: false }
      ],
      current: user?.subscription_tier === 'free' || !user?.subscription_tier,
      highlight: false
    },
    {
      id: 'basic_monthly',
      name: 'Basic',
      price: getPrice('basic_monthly', 'subscriptions', '$3.99'),
      originalPrice: discountPct > 0 ? '$3.99' : '$5.99',
      period: '/month',
      description: 'Finish your complete screenplay',
      limitedOffer: true,
      badge: 'MOST ECONOMICAL',
      badgeColor: 'emerald',
      features: [
        { text: '5 scene generations per day', included: true },
        { text: 'All 15 beats (Full 3-Act script)', included: true },
        { text: 'Full character development', included: true },
        { text: 'Script export (Fountain format)', included: true },
        { text: 'Collaborate with 2 writers', included: true },
        { text: 'Email support', included: true },
        { text: 'Pro Scene Studio', included: false },
        { text: 'Script Coverage Report', included: false }
      ],
      highlight: false,
      current: user?.subscription_tier === 'basic'
    },
    {
      id: 'pro_monthly',
      name: 'Pro',
      price: getPrice('pro_monthly', 'subscriptions', '$9.99'),
      originalPrice: discountPct > 0 ? '$9.99' : '$19.99',
      period: '/month',
      description: 'Professional tools for serious writers',
      limitedOffer: true,
      badge: 'MOST POPULAR',
      badgeColor: 'primary',
      features: [
        { text: 'Unlimited scene generations', included: true },
        { text: 'All 15 beats (Full 3-Act script)', included: true },
        { text: 'Pro Scene Studio (custom context, dialogue, tone)', included: true },
        { text: 'AI Script Coverage Report', included: true },
        { text: 'Advanced dialogue analysis', included: true },
        { text: 'Genre convention checker', included: true },
        { text: 'Unlimited collaborators', included: true },
        { text: 'Version history', included: true },
        { text: 'Priority support', included: true }
      ],
      current: user?.subscription_tier === 'pro'
    }
  ];

  const handleSubscribe = async (planId) => {
    if (planId === 'free') return;
    
    setLoading(planId);
    
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error('Payment initialization failed');
    } finally {
      setLoading(null);
    }
  };

  const handleOneTimePurchase = async (planId) => {
    setLoading(planId);
    
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/payments/create-onetime-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Checkout error:', errorData);
        toast.error(errorData.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment initialization failed');
    } finally {
      setLoading(null);
    }
  };

  const oneTimePlans = [
    {
      id: 'single_script',
      name: 'Script Pass',
      price: getPrice('single_script', 'one_time', '$14.99'),
      originalPrice: discountPct > 0 ? '$14.99' : undefined,
      description: 'Complete one full screenplay',
      features: [
        'Full 3-Act structure access',
        'All 15 story beats',
        'Unlimited scenes for this project',
        'Script export included',
        'Never expires'
      ],
      highlight: false,
      tier: 'basic'
    },
    {
      id: 'single_script_pro',
      name: 'Script Pass Pro',
      price: getPrice('single_script_pro', 'one_time', '$39.99'),
      originalPrice: discountPct > 0 ? '$39.99' : undefined,
      description: 'Full pro experience for one screenplay',
      features: [
        'Everything in Script Pass',
        'Pro Scene Studio (custom context)',
        'AI Script Coverage Report',
        'Advanced formatting options',
        'Never expires'
      ],
      highlight: true,
      savings: 'PRO FEATURES',
      tier: 'pro'
    },
    {
      id: 'three_script_bundle',
      name: '3-Script Bundle',
      price: getPrice('three_script_bundle', 'one_time', '$29.99'),
      originalPrice: discountPct > 0 ? '$29.99' : '$44.97',
      description: 'Best value for multiple projects',
      features: [
        'Everything in Script Pass',
        '3 complete screenplays',
        'Use anytime, never expires',
        'Save $15 vs buying separately'
      ],
      highlight: false,
      savings: 'Save 33%',
      tier: 'basic'
    },
    {
      id: 'three_script_pro_bundle',
      name: '3-Script Pro Bundle',
      price: getPrice('three_script_pro_bundle', 'one_time', '$99.99'),
      originalPrice: discountPct > 0 ? '$99.99' : '$119.97',
      description: 'Pro features for multiple screenplays',
      features: [
        'Everything in Script Pass Pro',
        '3 complete screenplays with Pro',
        'Pro Scene Studio for all 3',
        'Coverage Reports for all 3'
      ],
      highlight: false,
      savings: 'Save $20',
      tier: 'pro'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors" data-testid="sub-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Film className="w-6 h-6 text-primary" />
            <span className="font-bold tracking-tight text-white">Subscription</span>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Current Plan Banner */}
          {user?.subscription_tier && user.subscription_tier !== 'free' && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-10 p-6 bg-primary/10 border border-primary/30 flex items-center gap-4"
            >
              <Crown className="w-8 h-8 text-primary" />
              <div>
                <h3 className="text-white font-bold">You&apos;re on the {user.subscription_tier.charAt(0).toUpperCase() + user.subscription_tier.slice(1)} plan</h3>
                <p className="text-muted-foreground text-sm">Enjoy all your premium features</p>
              </div>
            </motion.div>
          )}

          {/* Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Zap className="w-12 h-12 text-primary mx-auto mb-4" />
              <h1 className="text-4xl font-bold tracking-tight text-white mb-4">Choose Your Plan</h1>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Start free with Act 1, then unlock the full screenplay experience
              </p>
            </motion.div>
          </div>

          {/* Regional Pricing Banner */}
          {discountPct > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-4 bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center gap-3 text-center"
              data-testid="regional-pricing-banner"
            >
              <Globe className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <p className="text-emerald-300 text-sm">
                <span className="font-bold">Regional pricing applied</span> — {discountPct}% off all plans for your region. Same features, fair local prices.
              </p>
            </motion.div>
          )}

          {/* Value Proposition Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12 p-6 bg-secondary/30 border border-white/10 text-center"
          >
            <p className="text-white text-lg">
              <span className="text-primary font-bold">Start your screenplay for free</span> — complete Act 1 and see your story come to life. 
              Ready to finish? Upgrade to continue through Acts 2 & 3.
            </p>
          </motion.div>

          {/* Tab Switcher */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex bg-secondary/30 border border-white/10 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('onetime')}
                className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-all rounded-l-md ${
                  activeTab === 'onetime'
                    ? 'bg-emerald-500 text-black'
                    : 'text-muted-foreground hover:text-white'
                }`}
                data-testid="tab-onetime"
              >
                <ShoppingBag className="w-4 h-4 inline mr-2" />
                One-Time Purchase
              </button>
              <button
                onClick={() => setActiveTab('subscription')}
                className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-all rounded-r-md ${
                  activeTab === 'subscription'
                    ? 'bg-primary text-black'
                    : 'text-muted-foreground hover:text-white'
                }`}
                data-testid="tab-subscription"
              >
                <Zap className="w-4 h-4 inline mr-2" />
                Subscribe & Save
              </button>
            </div>
          </div>

          {/* Subscription Plans Grid */}
          {activeTab === 'subscription' && (
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative p-8 transition-all duration-300 ${
                  plan.badge === 'MOST POPULAR'
                    ? 'bg-primary/10 border-2 border-primary' 
                    : plan.badge === 'MOST ECONOMICAL'
                      ? 'bg-emerald-500/10 border-2 border-emerald-500'
                      : plan.current
                        ? 'bg-white/5 border-2 border-white/30'
                        : 'bg-secondary/30 border border-white/5 hover:border-white/20'
                }`}
                data-testid={`sub-plan-${plan.id}`}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`text-xs font-bold uppercase tracking-wider px-4 py-1 ${
                      plan.badge === 'MOST POPULAR' 
                        ? 'bg-primary text-black' 
                        : 'bg-emerald-500 text-black'
                    }`}>
                      {plan.badge}
                    </span>
                  </div>
                )}
                
                {plan.current && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-white text-black text-xs font-bold uppercase tracking-wider px-4 py-1">
                      Current
                    </span>
                  </div>
                )}

                <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                
                {plan.limitedOffer && (
                  <div className="mb-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-primary bg-primary/20 px-2 py-1">
                      Limited Time Offer
                    </span>
                  </div>
                )}
                
                <div className="flex items-baseline gap-2 mb-6">
                  {plan.originalPrice && (
                    <span className="text-xl text-muted-foreground line-through">{plan.originalPrice}</span>
                  )}
                  <span className="text-4xl font-black text-white">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      {feature.included ? (
                        <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
                      )}
                      <span className={feature.included ? 'text-muted-foreground' : 'text-muted-foreground/50'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading === plan.id || plan.current || plan.id === 'free'}
                  className={`w-full font-bold uppercase tracking-widest text-xs py-4 ${
                    plan.badge === 'MOST POPULAR'
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                      : plan.badge === 'MOST ECONOMICAL'
                        ? 'bg-emerald-500 text-black hover:bg-emerald-400'
                        : plan.current
                          ? 'bg-white/20 text-white cursor-not-allowed'
                          : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid={`sub-btn-${plan.id}`}
                >
                  {loading === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : plan.current ? (
                    'Current Plan'
                  ) : plan.id === 'free' ? (
                    'Free Forever'
                  ) : (
                    'Subscribe'
                  )}
                </Button>
              </motion.div>
            ))}
          </div>
          )}

          {/* One-Time Purchase Section */}
          {activeTab === 'onetime' && (
            <div className="max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-10"
              >
                <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 px-4 py-2 mb-4">
                  <Gift className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 text-sm font-bold">No Subscription Required</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Buy Scripts, Not Subscriptions</h2>
                <p className="text-muted-foreground">
                  Pay once, own forever. Perfect for writers who want to complete specific projects without recurring fees.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {oneTimePlans.map((plan, index) => (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`relative p-6 transition-all duration-300 ${
                      plan.highlight 
                        ? 'bg-gradient-to-b from-yellow-500/20 to-yellow-500/5 border-2 border-yellow-500' 
                        : plan.tier === 'pro'
                          ? 'bg-yellow-500/5 border border-yellow-500/30 hover:border-yellow-500/50'
                          : 'bg-secondary/30 border border-white/5 hover:border-white/20'
                    }`}
                    data-testid={`onetime-plan-${plan.id}`}
                  >
                    {plan.savings && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 ${
                          plan.tier === 'pro' 
                            ? 'bg-yellow-500 text-black' 
                            : 'bg-emerald-500 text-black'
                        }`}>
                          {plan.savings}
                        </span>
                      </div>
                    )}

                    {plan.tier === 'pro' && !plan.savings && (
                      <div className="absolute -top-3 right-4">
                        <Crown className="w-5 h-5 text-yellow-500" />
                      </div>
                    )}

                    <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                    <p className="text-xs text-muted-foreground mb-3">{plan.description}</p>
                    
                    <div className="flex items-baseline gap-1 mb-4">
                      {plan.originalPrice && (
                        <span className="text-sm text-muted-foreground line-through">{plan.originalPrice}</span>
                      )}
                      <span className="text-2xl font-black text-white">{plan.price}</span>
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {plan.features.slice(0, 4).map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <CheckCircle className={`w-3 h-3 flex-shrink-0 mt-0.5 ${
                            plan.tier === 'pro' ? 'text-yellow-400' : 'text-emerald-400'
                          }`} />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleOneTimePurchase(plan.id)}
                      disabled={loading === plan.id}
                      className={`w-full font-bold uppercase tracking-widest text-xs py-3 ${
                        plan.tier === 'pro'
                          ? 'bg-yellow-500 text-black hover:bg-yellow-400' 
                          : 'bg-emerald-500/80 text-black hover:bg-emerald-400'
                      }`}
                      data-testid={`onetime-btn-${plan.id}`}
                    >
                      {loading === plan.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          {plan.tier === 'pro' ? (
                            <Crown className="w-3 h-3 mr-1" />
                          ) : (
                            <ShoppingBag className="w-3 h-3 mr-1" />
                          )}
                          Buy Now
                        </>
                      )}
                    </Button>
                  </motion.div>
                ))}
              </div>

              {/* Why One-Time? */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-10 p-6 bg-secondary/20 border border-white/5"
              >
                <h3 className="text-white font-bold mb-4 text-center">Why Choose One-Time Purchase?</h3>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <Tag className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No recurring charges. Pay once, write forever.</p>
                  </div>
                  <div>
                    <FileText className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Perfect for completing that one passion project.</p>
                  </div>
                  <div>
                    <Gift className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Script passes never expire — use them anytime.</p>
                  </div>
                </div>
              </motion.div>

              {/* Save More by Subscribing CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-8 p-6 bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/30 text-center"
              >
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-primary" />
                  <h3 className="text-white font-bold">Writing Multiple Scripts?</h3>
                </div>
                <p className="text-muted-foreground mb-4">
                  Subscribe and save! Get unlimited access to all features starting at just <span className="text-primary font-bold">$3.99/month</span>.
                </p>
                <button
                  onClick={() => setActiveTab('subscription')}
                  className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 px-6 py-2 text-primary font-bold hover:bg-primary/30 transition-colors"
                >
                  <Zap className="w-4 h-4" />
                  View Subscription Plans
                  <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            </div>
          )}

          {/* Feature Comparison */}
          <div className="mt-20 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-10">What You Get</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-6 bg-secondary/20 border border-white/5"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/20">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-white font-bold">3-Act Structure Guide</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  Follow the proven Hollywood framework. Free users complete Act 1 (Setup), 
                  subscribers unlock Acts 2 (Confrontation) and 3 (Resolution).
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-6 bg-secondary/20 border border-white/5"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/20">
                    <Sparkles className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-white font-bold">AI Script Coverage</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  Pro feature: Get professional-grade script analysis including pacing, 
                  dialogue assessment, character arcs, and market potential.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-6 bg-gradient-to-r from-purple-500/10 to-primary/10 border border-purple-500/30"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-500/20">
                    <Crown className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="text-white font-bold">Pro Scene Studio</h3>
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">PRO</span>
                </div>
                <p className="text-muted-foreground text-sm">
                  Take full creative control. Inject custom scene context, mandatory dialogue lines,
                  tone selection (tense, romantic, comedic, etc.), pacing control, and notes on what to avoid.
                  Perfect for professional writers who know exactly what they want.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-6 bg-secondary/20 border border-white/5"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/20">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-white font-bold">Collaboration</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  Write with partners. Basic allows 2 collaborators, Pro gives you 
                  unlimited team members with version history.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="p-6 bg-secondary/20 border border-white/5"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/20">
                    <Download className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-white font-bold">Professional Export</h3>
                </div>
                <p className="text-muted-foreground text-sm">
                  Export your screenplay in industry-standard Fountain format, 
                  compatible with Final Draft, Celtx, and other software.
                </p>
              </motion.div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-20 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-10">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {[
                {
                  q: 'What can I do for free?',
                  a: 'Free users can complete Act 1 (Setup) of their screenplay, create characters, and get 2 AI-generated scenes per day. It\'s a great way to start your story and see if MovieBot works for you.'
                },
                {
                  q: 'What is Pro Scene Studio?',
                  a: 'Pro Scene Studio is an advanced feature for Pro users that lets you inject custom context, mandatory dialogue lines, tone selection (tense, romantic, comedic, etc.), pacing control, and notes on what to avoid. It gives professional writers full creative control over AI scene generation.'
                },
                {
                  q: 'What is a Script Coverage Report?',
                  a: 'A professional analysis of your screenplay covering pacing, structure, character development, dialogue quality, genre adherence, and market potential. It\'s the same type of report used by Hollywood studios.'
                },
                {
                  q: 'Can I cancel anytime?',
                  a: 'Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period, and you\'ll keep access to Act 1 forever.'
                },
                {
                  q: 'What payment methods do you accept?',
                  a: 'We accept all major credit cards through our secure payment partner Stripe.'
                }
              ].map((faq, i) => (
                <div key={i} className="p-6 bg-secondary/20 border border-white/5">
                  <h3 className="text-white font-bold mb-2">{faq.q}</h3>
                  <p className="text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
